import { navFor } from '@/lib/navigation';
import type { Role } from '@/types/roles';

/**
 * The role trees a signed-in person may actually enter.
 *
 * This is NOT "all the roles that exist" — it is derived from the guards in
 * App.tsx, and today exactly one role can enter more than one tree: an admin
 * may work inside the recruiter screens (`allow={['recruiter', 'admin']}`).
 * Everyone else has one view, and the switcher does not render for them.
 *
 * It is a view switcher, not a role switcher: nobody's actual role changes,
 * their token is untouched, and every guard and rule still sees the role they
 * really have. Switching only chooses which of their own trees they are
 * looking at. A control that appeared to grant a role it cannot grant would be
 * the worst possible thing to put in a navigation bar.
 */
const VIEWS: Record<Role, Role[]> = {
  admin: ['admin', 'recruiter'],
  recruiter: ['recruiter'],
  recommender: ['recommender'],
  'ngo-rep': ['ngo-rep'],
  candidate: ['candidate'],
  guest: ['guest'],
};

/**
 * The union of every tree the person's roles can reach, in the order the roles
 * were granted. A union rather than a lookup for one role: someone who is both
 * a recruiter and a recommender has both, and picking one of their roles to ask
 * about would hide the other half of their own account.
 */
export function viewsFor(roles: Role[] | Role): Role[] {
  const list = Array.isArray(roles) ? roles : [roles];
  const out: Role[] = [];
  for (const role of list) {
    for (const view of VIEWS[role] || [role]) {
      if (!out.includes(view)) out.push(view);
    }
  }
  return out;
}

export function canUseView(roles: Role[] | Role, view: Role): boolean {
  return viewsFor(roles).includes(view);
}

/** Where a view starts — its first nav entry, the same rule homePathFor uses. */
export function homeOfView(view: Role): string {
  return navFor(view)[0].to;
}
